import sharedConstants from "@lib/shared_constants";

import type {
  WhatsAppMessageTemplateResponseComponents,
  WhatsAppMessageTemplateResponsePrefillContent,
} from "../engagementAppGroupIdWhatsAppMessageTemplatesExternalAccountId/types";

const {
  COMMUNICATION: {
    WHATS_APP: { MEDIA: mediaConstants },
  },
} = sharedConstants;

export type TemplateComponentHeaderFormatText = "text";
export type TemplateComponentHeaderFormatMedia = "image" | "video" | "document";

export type TemplateComponentHeader = {
  header_format: TemplateComponentHeaderFormatText | TemplateComponentHeaderFormatMedia;
  text?: string;
  examples?: string[];
};

export type TemplateComponentBody = {
  text: string;
  examples?: string[];
};

export type TemplateComponentFooter = {
  text: string;
};

export type TemplateComponentButtonTypeUrl = "url";
export type TemplateComponentButtonUrl = {
  button_type: TemplateComponentButtonTypeUrl;
  text: string;
  url: string;
  examples?: string[];
};

export type TemplateComponentButtonTypePhoneNumber = "phone-number";
export type TemplateComponentButtonPhoneNumber = {
  button_type: TemplateComponentButtonTypePhoneNumber;
  text: string;
  phone_number: string;
  examples?: string[];
};

export type TemplateComponentButtonTypeQuickReply = "quick-reply";
export type TemplateComponentButtonTypeCopyCode = "copy-code";
export type TemplateComponentButtonTypeOther =
  | TemplateComponentButtonTypeQuickReply
  | TemplateComponentButtonTypeCopyCode;
export type TemplateComponentButtonOther = {
  button_type: TemplateComponentButtonTypeQuickReply | TemplateComponentButtonTypeCopyCode;
  text: string;
  // Only meaningful for copy-code buttons; carries the per-template default offer code.
  copy_code?: string;
  examples?: string[];
};

// Meta's `limited_time_offer` component. `text` and `has_expiration` are Meta-facing
// template definition; `expiration` is editable per-send. `buildLimitedTimeOfferTemplate`
// routes the value onto `prefill_content.limited_time_offer_expiration` at submit, so it
// never ships on the wire components block — mirrors how `media_uri` rides the scene-level
// header component but persists at `prefill_content.header_media_uri`.
export interface WhatsAppTemplateLimitedTimeOfferComponent {
  text: string;
  has_expiration: boolean;
  expiration?: string;
}

export type TemplateComponentButton =
  | TemplateComponentButtonUrl
  | TemplateComponentButtonPhoneNumber
  | TemplateComponentButtonOther;

export interface TemplateComponentsCarouselCard {
  header?: TemplateComponentHeader;
  body?: TemplateComponentBody;
  buttons?: TemplateComponentButton[];
}

export interface TemplateComponents {
  header?: TemplateComponentHeader;
  body?: TemplateComponentBody;
  footer?: TemplateComponentFooter;
  buttons?: TemplateComponentButton[];
  carousel_cards?: TemplateComponentsCarouselCard[];
  limited_time_offer?: WhatsAppTemplateLimitedTimeOfferComponent;
}

export interface TemplatePrefillContentCarouselCard {
  header_media_type?: typeof mediaConstants.IMAGE.MEDIA_TYPE_KEY | typeof mediaConstants.VIDEO.MEDIA_TYPE_KEY;
  header_media_uri?: string;
  header_asset_id?: string;
  header_liquid_variables?: (string | null)[];
  body_liquid_variables?: (string | null)[];
  button_liquid_variables?: (string | null)[];
}

export interface TemplatePrefillContent {
  header_media_type?:
    | typeof mediaConstants.IMAGE.MEDIA_TYPE_KEY
    | typeof mediaConstants.VIDEO.MEDIA_TYPE_KEY
    | typeof mediaConstants.DOCUMENT.MEDIA_TYPE_KEY;
  header_media_uri?: string;
  header_asset_id?: string;
  header_liquid_variables?: (string | null)[];
  body_liquid_variables?: (string | null)[];
  button_liquid_variables?: (string | null)[];
  carousel_cards?: TemplatePrefillContentCarouselCard[];
  // Numeric string (ms since epoch) or a Liquid expression resolved per-recipient at send.
  // BE infers kind at runtime — same convention as header_media_uri.
  limited_time_offer_expiration?: string;
}

export interface PostWhatsAppMessageTemplateRequest {
  external_account_ids: string[];
  template: {
    id?: string;
    name: string;
    language: string;
    category: string;
    subtype: string;
    components?: TemplateComponents;
    prefill_content?: TemplatePrefillContent;
    /** Whether click tracking is enabled (maps to backend `cte` field) */
    click_tracking_enabled?: boolean;
    /** Custom domain for link shortening (maps to backend `cd` field) */
    custom_domain?: string;
  };
}

export interface MetaApiErrorResponse {
  error: {
    message: string;
    error_user_title?: string;
    error_user_msg?: string;
  };
}

export type PostWhatsAppMessageTemplateResponseCommonResult = {
  external_account_id?: string;
};

export type PostWhatsAppMessageTemplateResponseSuccessResult = {
  success: true;
};

export type PostWhatsAppMessageTemplateResponseErrorResult = {
  success: false;
  error: string;
  meta_api_response: string;
};

export type PostWhatsAppMessageTemplateResponseResult = PostWhatsAppMessageTemplateResponseCommonResult &
  (PostWhatsAppMessageTemplateResponseSuccessResult | PostWhatsAppMessageTemplateResponseErrorResult);

export interface PostWhatsAppMessageTemplateResponse {
  overall_success: boolean;
  results: PostWhatsAppMessageTemplateResponseResult[];
}

export interface PostEngagementAppGroupIdWhatsAppMessageTemplatesProps {
  appGroupId: string;
  requestBody: PostWhatsAppMessageTemplateRequest;
}

export interface PostSaveWhatsAppMessageTemplateAsDraftRequest {
  template: Partial<PostWhatsAppMessageTemplateRequest["template"]> & {
    external_account_id: string;
  };
}

export interface PostSaveWhatsAppMessageTemplateAsDraftResponse {
  template: {
    id: string;
    name: string;
    language?: string;
    category?: string;
  };
}

export interface PostEngagementAppGroupIdWhatsAppMessageTemplatesSaveAsDraftProps {
  appGroupId: string;
  requestBody: PostSaveWhatsAppMessageTemplateAsDraftRequest;
}

export interface GetWhatsAppMessageTemplateByIdResponse {
  template: {
    id: string;
    app_group_id: string;
    name: string;
    language?: string;
    category?: string;
    subtype?: string;
    external_account_id?: string;
    click_tracking_enabled?: boolean;
    custom_domain?: string;
    components?: WhatsAppMessageTemplateResponseComponents;
    prefill_content?: WhatsAppMessageTemplateResponsePrefillContent;
  };
}
